import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { SignInButton, UserButton, SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { api } from './utils/api';
import { useMeRole } from './hooks/useMeRole';
import { useAdmin } from './hooks/useAdmin';
import { CartProvider, useCart } from './contexts/CartContext';
import CartPanel from './components/CartPanel';
import BottomNav from './components/BottomNav';
import InstallPWA from './components/InstallPWA';
import NotificationBell from './components/NotificationBell';
import Profile from './pages/Profile';
import JoinMarket from './pages/JoinMarket';
import MyItems from './pages/MyItems';
import MyPurchases from './pages/MyPurchases';
import Marketplace from './pages/Marketplace';
import MarketDetail from './pages/MarketDetail';
import CityDetail from './pages/CityDetail';
import VendorPublic from './pages/VendorPublic';
import ItemDetail from './pages/ItemDetail';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import Admin from './pages/Admin';
import Search from './pages/Search';
import VendorLogin from './pages/VendorLogin';
import Account from './pages/Account';
import OwnerDashboard from './pages/OwnerDashboard';
import VendorReports from './pages/VendorReports';
import Pickup from './pages/Pickup';
import SignInRedirect from './pages/SignInRedirect';
import './App.css';

/**
 * When signed in: only redirect to garage seller flow when user hits a garage seller-only route without CityId.
 * Customer routes (home, search, my-purchases, etc.) are never redirected. User type from DB only.
 */
function OnboardingGuard({ children }) {
  const { getToken } = useAuth();
  const location = useLocation();
  const [garageSeller, setGarageSeller] = useState(null);
  const [loading, setLoading] = useState(true);

  const path = location.pathname;
  const publicRoutes = ['/', '/market', '/city', '/vendor', '/item', '/search', '/vendor/login', '/pickup'];
  const isPublicRoute = publicRoutes.some(route => path === route || path.startsWith('/market/') || path.startsWith('/city/') || path.startsWith('/vendor/') || path.startsWith('/item/') || path.startsWith('/search') || path.startsWith('/pickup')) && path !== '/vendor/reports';
  
  // Always call hooks unconditionally - but skip API call for public routes
  useEffect(() => {
    // For public routes, skip loading check entirely
    if (isPublicRoute || path === '/account' || path === '/my-purchases') {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api('/api/vendors/me', { getToken });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok) setGarageSeller(data);
        else if (!cancelled) setGarageSeller(null);
      } catch {
        if (!cancelled) setGarageSeller(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, location.pathname, isPublicRoute, path]);

  // For public routes, skip loading check entirely - render immediately
  if (isPublicRoute || path === '/account' || path === '/my-purchases') {
    return children;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }

  const hasCity = garageSeller?.cityId != null;
  const hasStoreName = (garageSeller?.storeName ?? '').trim() !== '';
  const hasStripeAccount = garageSeller?.stripeAccountId != null && garageSeller.stripeAccountId.trim() !== '';

  // Garage seller-only routes: require CityId or redirect to garage seller login
  const garageSellerOnlyRoutes = ['/profile', '/join-market', '/my-items', '/items', '/vendor/reports'];
  const isGarageSellerOnlyRoute = garageSellerOnlyRoutes.some(route => path === route || path.startsWith(route + '/'));
  if (!hasCity && isGarageSellerOnlyRoute) {
    return <Navigate to="/vendor/login" replace />;
  }

  if (hasCity && !hasStoreName && path !== '/profile') {
    return <Navigate to="/profile" replace />;
  }
  const stripeProtectedRoutes = ['/my-items', '/items/create', '/items/new'];
  const isStripeProtectedRoute = stripeProtectedRoutes.some(route => path.startsWith(route));
  if (hasCity && hasStoreName && isStripeProtectedRoute && !hasStripeAccount && path !== '/profile') {
    return <Navigate to="/profile?stripe=required" replace />;
  }

  return children;
}

/** Owner role removed - no longer needed in garage sale model */
function OwnerGuard({ children }) {
  return children;
}

/** Redirect admins to admin dashboard when they try to access any other route. Admins only see /admin. */
function AdminGuard({ children }) {
  const location = useLocation();
  const { isAdmin, loading } = useAdmin();
  const path = location.pathname;
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-500 dark:text-slate-400">
        Loading…
      </div>
    );
  }
  const adminPaths = ['/admin', '/admin/reports'];
  if (isAdmin && !adminPaths.includes(path)) {
    return <Navigate to="/admin" replace />;
  }
  return children;
}

function AmazonHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOwner, isVendor, loading: roleLoading } = useMeRole();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { getTotal } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const itemCount = getTotal();
  
  // Show header on every view except join-market and checkout result pages
  const noHeaderPaths = ['/join-market', '/checkout/success', '/checkout/cancel'];
  const isShowHeaderPath = !noHeaderPaths.includes(location.pathname);
  if (!isShowHeaderPath) {
    return null;
  }
  
  // Hide for owners/admins
  if ((!roleLoading && isOwner) || (!adminLoading && isAdmin)) {
    return null;
  }

  const handleCartClick = () => {
    window.dispatchEvent(new CustomEvent('openCart'));
  };

  const handleMenuClick = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleMenuClose = () => {
    setIsMenuOpen(false);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-40 w-full max-w-screen-md mx-auto">
        {/* Upper nav bar only - dark background */}
        <div className="bg-[#232F3E] text-white w-full">
          <div className="mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <button
            onClick={handleMenuClick}
            className="text-white shrink-0"
            aria-label="Menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex-1 flex justify-center min-w-0">
            <div className="flex items-center pointer-events-none">
              <span className="text-xl font-semibold text-white">Garage</span>
              <span className="text-xl font-semibold text-orange-400 ml-1">Hub</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="text-sm flex items-center gap-1 hover:text-orange-400 transition-colors">
                  <span>Sign in</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <div className="flex items-center gap-2">
                {isVendor && <NotificationBell isVendor={isVendor} />}
                <UserButton 
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: 'h-8 w-8',
                    },
                  }}
                />
              </div>
            </SignedIn>
            <button 
              onClick={handleCartClick}
              className="relative cursor-pointer"
              aria-label={`Shopping cart with ${itemCount} items`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>
          </div>
        </div>
        </div>
        {/* Link strip only for non-vendors (no garage sale yet) */}
        {!roleLoading && !isVendor && (
        <div className="bg-gray-100 border-b border-gray-200 pt-0.5 px-4 pb-1.5 text-center">
          <Link
            to="/vendor/login"
            onClick={handleMenuClose}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            Want to start your own garage sale? Click here
          </Link>
        </div>
        )}
      </div>

      {/* Hamburger Menu Drawer */}
      {isMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-[45] transition-opacity"
            onClick={handleMenuClose}
            aria-hidden="true"
          />
          <div className={`fixed top-0 left-0 h-full w-64 bg-[#232F3E] text-white z-50 transform transition-transform duration-300 ease-out shadow-2xl ${
            isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
            <div className="p-4 border-b border-white/20">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-xl font-semibold text-white">Garage</span>
                  <span className="text-xl font-semibold text-orange-400 ml-1">Hub</span>
                </div>
                <button 
                  onClick={handleMenuClose}
                  className="text-white hover:text-orange-400 transition-colors"
                  aria-label="Close menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <nav className="flex flex-col p-4">
              {!isVendor && (
              <Link
                to="/vendor/login"
                onClick={() => handleNavClick('/vendor/login')}
                className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3 text-orange-400 font-medium"
              >
                <span className="text-xl">🏪</span>
                <span>Want to start your own garage sale? Click here</span>
              </Link>
              )}
              <button
                onClick={() => handleNavClick('/')}
                className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
              >
                <span className="text-xl">🏠</span>
                <span>Home</span>
              </button>
              <button
                onClick={() => handleNavClick('/search')}
                className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
              >
                <span className="text-xl">🔍</span>
                <span>Search</span>
              </button>
              <SignedIn>
                <button
                  onClick={() => handleNavClick('/my-purchases')}
                  className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                >
                  <span className="text-xl">🛍️</span>
                  <span>My Purchases</span>
                </button>
                <button
                  onClick={() => handleNavClick('/account')}
                  className="text-left py-3 px-4 rounded-lg hover:bg-white/10 transition-colors flex items-center gap-3"
                >
                  <span className="text-xl">👤</span>
                  <span>Account</span>
                </button>
              </SignedIn>
              <SignedOut>
                <div className="py-3 px-4">
                  <SignInButton mode="modal">
                    <button className="w-full py-2 px-4 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition-colors">
                      Sign in
                    </button>
                  </SignInButton>
                </div>
              </SignedOut>
            </nav>
          </div>
        </>
      )}
    </>
  );
}

function SearchBar() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search garage sales..."
          className="w-full h-12 text-base px-4 pr-14 rounded-full border-0 bg-gray-100 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-400"
        />
        <button
          type="submit"
          className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 transition-colors flex items-center justify-center"
          aria-label="Search"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
      </div>
    </form>
  );
}

function App() {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const location = useLocation();
  const { isOwner, loading: roleLoading } = useMeRole();
  const { isAdmin, loading: adminLoading } = useAdmin();

  const handleCloseCart = () => {
    setIsCartOpen(false);
  };

  // Close cart when navigating (e.g. tapping Home, Search, Items, Profile in bottom nav)
  useEffect(() => {
    if (isCartOpen) setIsCartOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]); // Only close on pathname change, not when isCartOpen changes

  // Listen for custom cart open event from bottom nav
  useEffect(() => {
    const handleOpenCart = () => {
      setIsCartOpen(true);
    };
    window.addEventListener('openCart', handleOpenCart);
    return () => window.removeEventListener('openCart', handleOpenCart);
  }, []);

  const isVendorLoginPage = location.pathname === '/vendor/login';
  const isAccountPage = location.pathname === '/account';

  return (
    <CartProvider>
      <AppContent 
        isCartOpen={isCartOpen}
        handleCloseCart={handleCloseCart}
        isVendorLoginPage={isVendorLoginPage}
        isAccountPage={isAccountPage}
        isOwner={isOwner}
        roleLoading={roleLoading}
        isAdmin={isAdmin}
        adminLoading={adminLoading}
      />
    </CartProvider>
  );
}

function AppContent({ 
  isCartOpen, 
  handleCloseCart, 
  isVendorLoginPage, 
  isAccountPage,
  isOwner,
  roleLoading,
  isAdmin,
  adminLoading
}) {
  const location = useLocation();
  const noHeaderPaths = ['/join-market', '/checkout/success', '/checkout/cancel'];
  const addTopPadding = !noHeaderPaths.includes(location.pathname);

  return (
    <>
      <AmazonHeader />
      <div className={`max-w-screen-md mx-auto ${addTopPadding ? 'pt-28 md:pt-36' : ''} p-4 md:p-8 pb-28 overflow-x-hidden`}>
        <header className="flex flex-col gap-4 mb-8">
          {/* Global Search Bar - hidden for admins, My Items, Profile, My Purchases, Account, vendor pages, garage seller login, join-market, checkout pages, city pages */}
          {(adminLoading || !isAdmin) && location.pathname !== '/my-items' && location.pathname !== '/profile' && location.pathname !== '/my-purchases' && location.pathname !== '/account' && !location.pathname.startsWith('/vendor/') && !location.pathname.startsWith('/city/') && location.pathname !== '/join-market' && location.pathname !== '/checkout/success' && location.pathname !== '/checkout/cancel' && <SearchBar />}
          {/* Account bar only on the Account tab */}
          {isAccountPage && (
          <SignedIn>
            <OnboardingGuard>
              <>
                <div className="flex items-center justify-end w-full">
                  <UserButton
                    afterSignOutUrl="/"
                    appearance={{
                      elements: {
                        avatarBox: 'h-14 w-14',
                      },
                    }}
                  />
                </div>
              </>
            </OnboardingGuard>
          </SignedIn>
          )}
        </header>
        <AdminGuard>
        <OwnerGuard>
        <Routes>
          <Route path="/" element={<Marketplace />} />
          <Route path="/search" element={<Search />} />
          <Route path="/market/:id" element={<MarketDetail />} />
          <Route path="/city/:id" element={<CityDetail />} />
          <Route path="/vendor/:id" element={<VendorPublic />} />
          <Route path="/item/:id" element={<ItemDetail />} />
          <Route
            path="/profile"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <Profile />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route path="/vendor/login" element={<VendorLogin />} />
          <Route path="/sign-in" element={<SignInRedirect />} />
          <Route
            path="/join-market"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <JoinMarket />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/pickup" element={<Pickup />} />
          <Route
            path="/my-items"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <MyItems />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/my-purchases"
            element={
              <>
                <SignedIn>
                  <MyPurchases />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/account"
            element={
              <>
                <SignedIn>
                  <Account />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/owner"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <OwnerDashboard />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/vendor/reports"
            element={
              <>
                <SignedIn>
                  <OnboardingGuard>
                    <VendorReports />
                  </OnboardingGuard>
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/admin"
            element={
              <>
                <SignedIn>
                  <Admin />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <>
                <SignedIn>
                  <Admin />
                </SignedIn>
                <SignedOut>
                  <Navigate to="/" replace />
                </SignedOut>
              </>
            }
          />
        </Routes>
        </OwnerGuard>
        </AdminGuard>
        <CartPanel isOpen={isCartOpen} onClose={handleCloseCart} />
        <BottomNav onCloseCart={handleCloseCart} isCartOpen={isCartOpen} />
        <InstallPWA />
      </div>
    </>
  );
}

export default App;
